import DjangoMessages from 'components/DjangoMessages';
import FormActions from 'components/Form/FormActions';
import SubmitButton from 'components/Form/SubmitButton';
import Information from 'components/Information';
import SectionHeader from 'components/SectionHeader';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {c, jt, t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiErrorAxios, onApiSuccess} from 'utils/request-axios/utils';

import useRevokeEmployee from './services';
import {MemberRevokeBackendData} from './types';

const RevokeApiEmployee = () => {
    const {
        data: {apiName, isApi, partnerName},
    } = useBackend<MemberRevokeBackendData>();

    if (!isApi) return null;

    const boldApiName = <b>{apiName}</b>;
    const boldPartnerName = <b>{partnerName}</b>;

    return (
        <>
            <SectionHeader level={1} title={t`Revoke ${apiName}`} />
            <p>
                {jt`You are about to revoke access from ${boldApiName}. They will no longer have access to ${boldPartnerName} data.`}
            </p>
            <FormActions className="mt-4">
                <SubmitButton kind="destructive">{c('Revoke')
                    .t`Revoke Access`}</SubmitButton>
            </FormActions>
        </>
    );
};

const RevokeEmployee = () => {
    const {
        data: {
            accountName,
            employeeName,
            isApi,
            partnerName,
            scheduledSendingOrderCount,
        },
    } = useBackend<MemberRevokeBackendData>();

    if (isApi) return null;

    const boldEmployeename = <b>{employeeName}</b>;
    const boldAccountName = <b>{accountName}</b>;
    const boldPartnerName = <b>{partnerName}</b>;
    const boldScheduledSendingOrderCount = <b>{scheduledSendingOrderCount}</b>;

    return (
        <>
            <SectionHeader level={1} title={t`Revoke Team Member`} />
            <div className="mt-4">{t`Team Member details:`}</div>
            <ul>
                <li>{jt`Name: ${boldEmployeename}`}</li>
                {partnerName && accountName && (
                    <li>
                        {jt`Company: ${boldPartnerName} ${boldAccountName}`}
                    </li>
                )}
                {partnerName && !accountName && (
                    <li>{jt`Company: ${boldPartnerName}`}</li>
                )}
            </ul>
            <p>{jt`After revoking ${boldEmployeename}:`}</p>
            <ul>
                <li>{t`The Member will no longer have access to the data of the Company.`}</li>
                <li>{t`Documents and history events created by the Member will be preserved.`}</li>
            </ul>
            {scheduledSendingOrderCount && scheduledSendingOrderCount > 0 ? (
                <Information className="mt-4" type="alert">
                    {jt`Note: Revoking will automatically discard ${boldScheduledSendingOrderCount} Scheduled Send(s) created by ${boldEmployeename}.`}
                </Information>
            ) : null}
            <FormActions className="mt-4">
                <SubmitButton kind="destructive">{t`Revoke Access`}</SubmitButton>
            </FormActions>
        </>
    );
};

const MemberRevoke = () => {
    const methods = useForm();
    const {
        data: {mid},
    } = useBackend<MemberRevokeBackendData>();

    const {mutateAsync} = useRevokeEmployee();

    const handleSubmit = () => {
        mutateAsync({mid})
            .then(
                onApiSuccess({
                    onSuccess: () => setUrl(window.Urls.employee_list_view()),
                }),
            )
            .catch(onApiErrorAxios(methods));
    };

    return (
        <div>
            <DjangoMessages />
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <RevokeApiEmployee />
                    <RevokeEmployee />
                </form>
            </FormProvider>
        </div>
    );
};

export default MemberRevoke;
